/**
 * Team form
 * Handles captain role selection and looking-for roles for team listings and the team-only parts of the form:
 * the team name and its preview
 */

import { byId, all, setText } from '../../utils/dom';

const lookingForRole = (slug) => document.querySelector(`#pf-looking-for .pf-role[data-slug="${slug}"]`);

const TeamFormStrategy = {
    /**
     * Initialize state for team roles
     */
    initState(form) {
        form.captainRoleId = null;
        form.lookingForRoles = {};
    },


    /**
     * Initialize looking-for checkboxes
     */
    initLookingFor(form) {
        // Initialize all roles as "needed" (selected)
        all('#pf-looking-for .pf-role').forEach(function(role) {
            form.lookingForRoles[role.dataset.slug] = true;
        });
    },

    /**
     * Handle role button click (captain selection)
     */
    handleRoleClick(form, btn) {
        const roleId = Number(btn.dataset.role);
        const roleSlug = btn.dataset.slug;

        // Toggle selection
        if (form.captainRoleId === roleId) {
            // Deselect
            form.captainRoleId = null;
        } else {
            form.captainRoleId = roleId;
        }

        this.render(form);

        // Update looking-for: exclude captain role
        this.updateLookingForAfterCaptainChange(form, roleSlug);
    },

    /**
     * Render captain role UI
     */
    render(form) {
        all('#pf-captain-roles .pf-role').forEach(function(btn) {
            btn.classList.toggle('pf-role--selected', Number(btn.dataset.role) === form.captainRoleId);
        });

        // Update hidden input
        form.setInput('leader_role_id', form.captainRoleId || '');

        // Hide error if selected
        if (form.captainRoleId) {
            this.showError(false);
        }

        // Show/hide looking-for section based on captain selection
        byId('pf-looking-for-section')?.classList.toggle('hidden', !form.captainRoleId);

        // Update footer buttons
        form.updatePublishButton();
    },

    /**
     * Update looking-for after captain role change
     */
    updateLookingForAfterCaptainChange(form, previousSlug) {
        // Show all items first
        all('#pf-looking-for .pf-role').forEach((role) => role.classList.remove('hidden'));

        // Hide the item for captain's role
        if (form.captainRoleId) {
            const captainSlug = this.getCurrentCaptainSlug(form);

            // Hide captain's role from looking-for
            lookingForRole(captainSlug)?.classList.add('hidden');

            // Uncheck captain's role
            form.lookingForRoles[captainSlug] = false;
            this.updateLookingForCheckbox(form, captainSlug);
        }

        // Show previous captain's role if it was hidden
        if (previousSlug && previousSlug !== this.getCurrentCaptainSlug(form)) {
            lookingForRole(previousSlug)?.classList.remove('hidden');
        }
    },

    /**
     * Get current captain role slug
     */
    getCurrentCaptainSlug(form) {
        if (!form.captainRoleId) return null;
        return document.querySelector(`#pf-captain-roles .pf-role[data-role="${form.captainRoleId}"]`)?.dataset.slug;
    },

    /**
     * Toggle looking-for role selection
     */
    toggleLookingFor(form, item) {
        const slug = item.dataset.slug;

        // Don't allow toggling captain's role
        if (slug === this.getCurrentCaptainSlug(form)) return;

        form.lookingForRoles[slug] = !form.lookingForRoles[slug];
        this.updateLookingForCheckbox(form, slug);
    },

    /**
     * Update looking-for role UI
     */
    updateLookingForCheckbox(form, slug) {
        const item = lookingForRole(slug);
        if (!item) return;
        const isChecked = form.lookingForRoles[slug];

        item.classList.toggle('pf-role--selected', isChecked);
        item.querySelectorAll('input[type="checkbox"]').forEach((input) => input.checked = isChecked);
    },

    /**
     * Validate that captain role is selected
     */
    validate(form) {
        this.showError(!form.captainRoleId);

        if (!form.captainRoleId) {
            byId('pf-captain-roles')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        return !!form.captainRoleId;
    },

    /**
     * Show or hide the "pick your role" error and highlight the captain roles
     */
    showError(show) {
        all('.pf-roles-error').forEach((el) => el.classList.toggle('hidden', !show));
        byId('pf-captain-roles')?.classList.toggle('pf-roles--error', show);
    },

    /**
     * Generate team name from summoner name
     */
    generateTeamName(form, summonerName) {
        const teamNameInput = form.form.querySelector('#pf-team-name');
        if (!teamNameInput || teamNameInput.value) return; // Don't overwrite if already filled

        // Extract base name (before # if present)
        let baseName = summonerName.split('#')[0].trim();

        // Generate team name: "Team <<BaseName>>"
        const teamName = 'Team \u00AB' + baseName + '\u00BB';

        teamNameInput.value = teamName;
        // The preview listens to input on the document
        teamNameInput.dispatchEvent(new Event('input', {bubbles: true}));
    },

    /**
     * After the summoner is found: suggest a team name from it
     */
    onSummonerFound(form, data) {
        this.generateTeamName(form, data.name);
    },

    /**
     * Preview card: the team name as typed (the promote form renders the team's name on the server)
     */
    renderPreview(form) {
        if (form.isPromote) return;
        setText('pf-pv-team-name', byId('pf-team-name')?.value || '');
    }
};

export default TeamFormStrategy;
